<template>
  <div
    class="search-widget"
    :class="[
      { 'tgmag-searchwidget--init': search.status === 'INITIALISE' },
      { 'tgmag-searchwidget--lock': search.isLoading }
    ]"
  >
    <spinner
      v-if="search.status === 'INITIALISE'"
      class="tgmag-searchwidget__init-spinner"
      role="status"
    />
    <div class="tgmag-searchwidget__group">
      <JourneyModes
        :toggles="modes"
        :value="query.mode"
        @input="handleModeChange"
      />
      <spacer :spacing="15" />
      <JourneyRoute
        :value="query.orientation"
        :transfers-mode="metaData.transfersMode"
        :tenant-id="metaData.tenantId"
        :routes-prepop-options="prepopulate.routes"
        @origin="handleLocationChange('origin', $event)"
        @destination="handleLocationChange('destination', $event)"
        @input="handleOrientationChange"
        @componentReady="formReady"
      />
      <spacer :spacing="15" />
      <JourneyDateRange
        :mode="query.mode"
        :label-prefix="labelPrefix"
        @single="handleDateChange('origin', $event)"
        @return="handleDateChange('destination', $event)"
      />
      <spacer :spacing="15" />
      <Passengers v-model="query.passengers" />

      <button-link
        class="btn-submit"
        :title="search.buttonTitle"
        :loading="isLoading"
        :is-disabled="isDisabled"
        @click="!isDisabled ? perform() : null"
      >
        Continue
      </button-link>
    </div>
  </div>
</template>

<script>
import "../../assets/scss/fonts.scss";
import Vue from "vue";
import axios from "axios";
import qs from "qs";
import moment from "moment";
import Vuelidate from "vuelidate";
import JourneyModes from "../JourneyModes";
import JourneyRoute from "../JourneyRoute";
import Spacer from "../Utilities/Spacer.vue";
import ButtonLink from "../Button/Button.vue";
import Passengers from "../Passengers";
import JourneyDateRange from "../JourneyDateRange";
import Spinner from "../Spinner";
import { isValidDateTime } from "./validation.js";

import { required, requiredIf, integer } from "vuelidate/lib/validators";
import { JOURNEY_ORIENTATION_TO } from "../JourneyRoute/types.js";
import {
  JOURNEY_MODE_RETURN,
  JOURNEY_MODE_SINGLE
} from "../JourneyModes/types.js";

import { TRANSFER_MODES } from "./config.js";

Vue.use(Vuelidate);
const MIN_ADULT_AGE = 17;
export default {
  name: "TransfersSearchWidget",
  components: {
    JourneyModes,
    JourneyRoute,
    Spacer,
    ButtonLink,
    Passengers,
    JourneyDateRange,
    Spinner
  },
  props: {
    iataCode: {
      type: String,
      validator(value) {
        return /[A-Z]{3}/.test(value);
      },
      default: "MAN"
    }
  },
  created() {},
  mounted() {
    if (window.self !== window.top) {
      const config = qs.parse(window.location.search.substring(1));
      const self = this;
      self.validateIframeAttributes(config);
      self.loadPopularRoutes();
      window.addEventListener("load", () => {
        self.$v.$touch();
      });
    } else {
      window.addEventListener("load", () => {
        this.validateMeta();
        this.loadPopularRoutes();
        this.$v.$touch();
      });
    }
  },
  data() {
    return {
      icons: {},
      redirecting: false,
      MIN_ADULT_AGE,
      searchId: null,
      isLoading: false,
      prepopulate: {
        routes: []
      },
      query: {
        origin: {
          location: {
            coordinates: {}
          },
          date: null
        },
        destination: {
          location: {
            coordinates: {}
          },
          date: null
        },
        mode: JOURNEY_MODE_SINGLE,
        orientation: JOURNEY_ORIENTATION_TO,
        passengers: [{ age: null }]
      },
      search: {
        errorStates: {},
        status: "INITIALISE",
        buttonTitle: "Please fill out the fields above"
      },
      modes: [
        {
          label: "One way",
          code: JOURNEY_MODE_SINGLE,
          icon: "single",
          disabled: false
        },
        {
          label: "Return",
          code: JOURNEY_MODE_RETURN,
          icon: "return",
          disabled: false
        }
      ],
      metaData: {
        transfersMode: null,
        tenantId: null,
        tenantPlatform: null
      }
    };
  },
  watch: {
    query: {
      deep: true,
      handler() {
        this.$v.query.$touch();
      }
    }
  },
  validations() {
    let validation = {
      query: {
        origin: {
          location: {
            address: { required }
          },
          date: {
            required,
            isValidDateTime
          }
        },
        destination: {
          location: {
            address: { required }
          }
        },
        passengers: {
          $each: {
            age: {
              integer,
              required: requiredIf(function(nestedModel) {
                return nestedModel.age === "";
              })
            }
          }
        }
      }
    };

    function greaterThanOrigin() {
      return moment(this.query.destination.date).isAfter(
        this.query.origin.date
      );
    }

    if (this.query.mode == JOURNEY_MODE_RETURN) {
      validation.query.destination.date = {
        required,
        isValidDateTime,
        greaterThanOrigin
      };
    }

    return validation;
  },
  computed: {
    redirectUrl() {
      let url = this.metaData.tenantPlatform.includes("/transfers")
        ? this.metaData.tenantPlatform
        : this.metaData.tenantPlatform + "/transfers";
      if (!url.endsWith("/")) {
        // Adds trialing slash if none.
        url = url + "/";
      }
      url = `${url}?searchId=${this.searchId}`;
      return url.toString();
    },
    isDisabled() {
      return this.$v.$invalid;
    },
    searchEndpoint() {
      return (
        process.env.VUE_APP_ENDPOINT_SEARCH_URI ||
        "https://search.test.transfers.maginfrastructure.com/api/v1/Search"
      );
    },
    searchEndpointData() {
      return {
        journey: this.searchEndpointDataJourney
      };
    },
    searchEndpointDataJourney() {
      let obj = {};
      obj.originLocation = {
        iata: this.query.origin.location.iata,
        coordinates: {
          latitude: this.query.origin.location.coordinates.latitude,
          longitude: this.query.origin.location.coordinates.longitude
        },
        address: this.query.origin.location.address,
        country: this.query.origin.country,
        countryCode: this.query.origin.countryCode
      };
      obj.destinationLocation = {
        iata: this.query.destination.location.iata,
        coordinates: {
          latitude: this.query.destination.location.coordinates.latitude,
          longitude: this.query.destination.location.coordinates.longitude
        },
        address: this.query.destination.location.address,
        country: this.query.destination.country,
        countryCode: this.query.destination.countryCode
      };
      if (this.query.orientation === JOURNEY_ORIENTATION_TO) {
        obj.outboundDateType = "DepartingFlightTime";
        obj.returnDateType = "ArrivingFlightTime";
      } else {
        obj.outboundDateType = "ArrivingFlightTime";
        obj.returnDateType = "DepartingFlightTime";
      }
      obj.outboundDate = this.query.origin.date;
      obj.returnDate = null;
      if (this.query.mode === JOURNEY_MODE_RETURN) {
        obj.returnDate = this.query.destination.date;
      } else if (this.query.mode === JOURNEY_MODE_SINGLE) {
        obj.returnDateType = null;
      }
      obj.passengers = this.query.passengers;

      return obj;
    },
    labelPrefix() {
      let going = {
        to: "Going to the airport",
        from: "Returning from the airport",
        dateTo: "Date",
        dateFrom: "Date",
        timeTo: "Select time",
        timeFrom: "Select time",
        tooltipTextTo:
          "Please enter the time you want to depart for the airport.",
        tooltipTextFrom: "Please enter your flight arrival time"
      };

      let arriving = {
        to: "Arriving at the airport",
        from: "Returning to the airport",
        dateTo: "Date",
        dateFrom: "Date",
        timeTo: "Select time",
        timeFrom: "Select time",
        tooltipTextTo: "What time does your flight land?",
        tooltipTextFrom:
          "Please enter the time you want to depart for the airport."
      };

      return this.query.orientation === JOURNEY_ORIENTATION_TO
        ? going
        : arriving;
    }
  },
  methods: {
    validateIframeAttributes(config) {
      try {
        const transfersMode = config["X-MAGO-TRANSFERS-MODE"];
        const tenantId = config["X-MAGO-TENANT-ID"];
        const tenantPlatform = config["X-MAGO-TENANT-PLATFORM"];
        this.metaData.transfersMode =
          typeof transfersMode !== "undefined" &&
          TRANSFER_MODES.includes(transfersMode)
            ? transfersMode
            : null;
        this.metaData.tenantId =
          typeof tenantId !== "undefined" ? tenantId : null;
        this.metaData.tenantPlatform =
          typeof tenantPlatform !== "undefined"
            ? tenantPlatform
            : "https://travel.manchesterairport.co.uk";
      } catch (e) {
        this.handleConfigError(e);
      }
    },
    validateMeta() {
      try {
        const transfersMode = document
          .getElementsByName("X-MAGO-TRANSFERS-MODE")[0]
          .getAttribute("content");
        const tenantId = document
          .getElementsByName("X-MAGO-TENANT-ID")[0]
          .getAttribute("content");
        const tenantPlatform = document
          .getElementsByName("X-MAGO-TENANT-PLATFORM")[0]
          .getAttribute("content");
        this.metaData.transfersMode =
          typeof transfersMode !== "undefined" &&
          TRANSFER_MODES.includes(transfersMode)
            ? transfersMode
            : null;

        this.metaData.tenantId =
          typeof tenantId !== "undefined" ? tenantId : null;
        this.metaData.tenantPlatform =
          typeof tenantPlatform !== "undefined"
            ? tenantPlatform
            : "https://travel.manchesterairport.co.uk";
      } catch (e) {
        this.handleConfigError(e);
      }
    },
    handleConfigError() {
      /* eslint-disable no-console */
      const metaData = {
        "X-MAGO-TRANSFERS-MODE": this.metaData.transfersMode,
        "X-MAGO-TENANT-ID": this.metaData.tenantId,
        "X-MAGO-TENANT-PLATFORM": this.metaData.tenantPlatform
      };
      console.error("Missing or invalid Transfer Mode or Tenant ID meta data");
      console.log(metaData);
      /* eslint-enable no-console */
    },
    perform() {
      this.isLoading = true;

      return axios({
        url: this.searchEndpoint,
        method: "post",
        headers: {
          "Access-Control-Allow-Origin": "*",
          "X-MAGO-TENANT-ID": this.metaData.tenantId
        },
        data: this.searchEndpointData
      })
        .then(({ data }) => {
          this.searchId = data;
        })
        .then(this.redirectToResults)
        .catch(err => {
          window.alert(`Search error:\n${err}`);
          this.isLoading = false;
        });
    },
    redirectToResults() {
      this.redirecting = true;
      if (window != window.top) {
        window.top.location.href = this.redirectUrl;
      } else {
        window.location.href = this.redirectUrl;
      }
    },
    handleModeChange(mode) {
      this.query.mode = mode;
    },
    handleOrientationChange(orientation) {
      this.query.orientation = orientation;
    },
    handleLocationChange(journey, data) {
      if (!data) {
        this.query[journey].location = null;
        return;
      }
      if (data.iata) {
        data.location.iata = data.iata;
      }
      this.query[journey].location = data.location;
    },
    handleDateChange(journey, date) {
      const iso8601Format = "YYYY-MM-DDTHH:mm:ss.SSS"; // The base format is to use a datetime with no offset.
      const formatDate = moment(date).format(iso8601Format);
      this.query[journey].date = formatDate;
    },
    loadPopularRoutes() {
      if (this.metaData.tenantId === "7C8BB1AD-60E3-4597-A9F0-756F2FD17327") {
        /*
         ** Popular list for STN
         */
        this.prepopulate.routes = [
          {
            type: "Address",
            location: {
              iata: null,
              coordinates: {
                latitude: 51.5073509,
                longitude: -0.1277583
              },
              address: {
                line1: "Charing Cross",
                line2: null,
                postcode: null,
                locality: "London",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "London",
            description: "Charing Cross, London, England, United Kingdom",
            label: "London"
          },
          {
            type: "transport-train",
            location: {
              iata: null,
              coordinates: {
                latitude: 51.518005,
                longitude: -0.081889
              },
              address: {
                line1: "London Liverpool Street",
                line2: null,
                postcode: null,
                locality: "London",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "London Liverpool Street",
            description:
              "London Liverpool Street Railway Station (LST), Bishopsgate, London, England, United Kingdom",
            label: "London Liverpool Street"
          },
          {
            type: "transport-train",
            location: {
              iata: null,
              coordinates: {
                latitude: 51.49523934375,
                longitude: -0.1449815625
              },
              address: {
                line1: "London Victoria",
                line2: null,
                postcode: null,
                locality: "London",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "London Victoria",
            description:
              "London Victoria station, 50 Buckingham Palace Rd, London, England, United Kingdom",
            label: "London Victoria"
          },
          {
            type: "transport-train",
            location: {
              iata: null,
              coordinates: { latitude: 51.522961, longitude: -0.157204 },
              address: {
                line1: "London Baker Street",
                line2: null,
                postcode: null,
                locality: "London",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "London Baker Street",
            description:
              "Baker Street London Underground Station, Marylebone Rd, London, England, United Kingdom",
            label: "London Baker Street"
          }
        ];
      } else if (
        this.metaData.tenantId === "2C7719B6-3984-476F-8AB1-B92C17EE88F5"
      ) {
        /*
         ** Popular list for MAN
         */
        this.prepopulate.routes = [
          {
            type: "Address",
            location: {
              iata: null,
              coordinates: { latitude: 53.46667, longitude: -2.23333 },
              address: {
                line1: null,
                line2: null,
                postcode: null,
                locality: "Manchester",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "Manchester",
            description:
              "Manchester, Greater Manchester, England, United Kingdom",
            label: "Manchester"
          }
        ];
      } else if (
        this.metaData.tenantId === "8BECE0B9-9401-4378-953C-41E5EFD27266"
      ) {
        /*
         ** Popular list for EMA
         */
        this.prepopulate.routes = [
          {
            type: "Address",
            location: {
              iata: null,
              coordinates: { latitude: 52.955, longitude: -1.14917 },
              address: {
                line1: null,
                line2: null,
                postcode: null,
                locality: "Nottingham",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "Nottingham",
            description:
              "Nottingham, City Of Nottingham, England, United Kingdom",
            label: "Nottingham"
          },
          {
            type: "Address",
            location: {
              iata: null,
              coordinates: { latitude: 52.48333, longitude: -1.9 },
              address: {
                line1: null,
                line2: null,
                postcode: null,
                locality: "Birmingham",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "Birmingham",
            description: "Birmingham, West Midlands, England, United Kingdom",
            label: "Birmingham"
          },
          {
            type: "Address",
            location: {
              iata: null,
              coordinates: { latitude: 53.46667, longitude: -2.23333 },
              address: {
                line1: null,
                line2: null,
                postcode: null,
                locality: "Manchester",
                country: "United Kingdom",
                countryCode: "GB"
              }
            },
            name: "Manchester",
            description:
              "Manchester, Greater Manchester, England, United Kingdom",
            label: "Manchester"
          }
        ];
      }
    },
    formReady() {
      /*
       ** Form is ready for the user.
       */
      this.search.status = "PENDING";
    }
  }
};
</script>

<style lang="scss" scoped>
.search-widget {
  position: relative;
  background-color: #ffffff;
  color: #032f61;
  display: block;
  max-width: 408px;
  width: 100%;
  box-shadow: 0 0 8px 0 rgba(29, 120, 165, 0.35);
  border-radius: 8px;
  padding: 12px 30px;
  font-family: sans-serif;
  @include media-breakpoint-down(md) {
    padding: 12px 15px;
  }
}

.tgmag-searchwidget--init .tgmag-searchwidget__group {
  opacity: 0.2;
  pointer-events: none;
  user-select: none;
}

.tgmag-searchwidget__init-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
}
.search-widget >>> input {
  font-family: sans-serif;
}

.search-widget .btn-submit {
  width: 100%;
  text-align: center;
}
</style>
